"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { ResultTable } from "@/components/tools/stats-descriptive/ui";
import {
  GroupedPlot,
  GroupedWorking,
  headline,
  statisticRows,
  tableRows,
  tableTotals,
} from "@/components/tools/stats-descriptive-more/grouped-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";
import {
  type BoundaryMode,
  groupedReport,
  isGroupedReport,
  parseClassLines,
} from "@/lib/tool-kit/stats-descriptive-more/grouped";

/** English strings. A translated content file overrides these through `ui`. */
export const GROUPED_DATA_CALCULATOR_STRINGS = {
  dataLabel: "Your table — one class per line: lower–upper, frequency",
  dataPlaceholder: "0-10, 3\n10-20, 7\n20-30, 12\n30-40, 14\n40-50, 9",
  classesDetected: "one: class read|other: classes read",
  skippedHint: "one: , line {lines} skipped|other: , lines {lines} skipped",
  boundaryLabel: "Class boundaries",
  boundaryDetect: "Detect from the gaps (10–19, 20–29 become 9.5–19.5, …)",
  boundaryWritten: "Use the limits exactly as written",
  tryExample: "Try an example",
  tryLimits: "Try class limits with gaps",
  exampleData: "0-10, 3\n10-20, 7\n20-30, 12\n30-40, 14\n40-50, 9",
  exampleLimits: "10-19, 6\n20-29, 14\n30-39, 22\n40-49, 11\n50-59, 5",
  clear: "Clear",
  enterPrompt:
    "Enter at least one class with a frequency — for example “10-20, 5” — to see the mean, median, mode and standard deviation.",
  overlap:
    "Two classes overlap. Check that each class starts at or after the end of the one before it.",
  noData: "The frequencies add up to zero, so there is nothing to summarise.",
  unequalNotice:
    "The class widths differ. The median and quartiles still interpolate correctly; the mode uses frequency density (frequency ÷ width) to choose the modal class.",
  results: "Results",
  cardN: "Total frequency (n)",
  cardMean: "Mean",
  cardMedian: "Median",
  cardMode: "Mode",
  cardSd: "Standard deviation (sample)",
  cardVariance: "Variance (sample)",
  modeSeparator: ", ",
  notAvailable: "—",
  tableTitle: "Your frequency table",
  colClass: "Class",
  colBoundaries: "Boundaries",
  colFreq: "Frequency f",
  colMidpoint: "Midpoint x",
  colFx: "f × x",
  colFx2: "f × x²",
  colCumulative: "Cumulative f",
  total: "Total",
  rangeTemplate: "{from}–{to}",
  statsTitle: "All the statistics",
  colStatistic: "Statistic",
  colValue: "Value",
  statMean: "Mean",
  statMedian: "Median (50th percentile)",
  statQ1: "First quartile Q1",
  statQ3: "Third quartile Q3",
  statIqr: "Interquartile range",
  statMode: "Mode",
  statVarianceSample: "Variance, sample (n − 1)",
  statSdSample: "Standard deviation, sample (n − 1)",
  statVariancePop: "Variance, population (n)",
  statSdPop: "Standard deviation, population (n)",
  plotTitle: "Histogram",
  plotLabel:
    "Histogram of the grouped data with the mean at {mean}, the median at {median} and the mode at {mode}.",
  plotYLabel: "Frequency",
  plotYDensity: "Frequency density",
  markMean: "Mean",
  markMedian: "Median",
  markMode: "Mode",
  plotNote:
    "Bars are drawn over the class boundaries. Lines mark the mean, the median and the first mode.",
  workingTitle: "How each statistic was found",
  workBoundaries:
    "The class limits leave a gap of {gap}, so each boundary sits {half} outside its limit: {example}.",
  workMean: "Mean = Σfx ÷ n = {sumFx} ÷ {n} = {mean}",
  workVariance:
    "Sample variance = (Σfx² − (Σfx)² ÷ n) ÷ (n − 1) = ({sumFx2} − {sumFx}² ÷ {n}) ÷ ({n} − 1) = {variance}",
  workVarianceOne:
    "Only one value, so the sample variance (n − 1) is undefined; the population variance is {variance}.",
  workSd: "Standard deviation = √{variance} = {sd}",
  workQuantile:
    "{name}: {p} × n = {target}. The class {from}–{to} is the first to reach it (cumulative before = {cf}, f = {f}, width = {w}) → {from} + (({target} − {cf}) ÷ {f}) × {w} = {value}",
  nameMedian: "Median",
  nameQ1: "Q1",
  nameQ3: "Q3",
  workMode:
    "Mode: modal class {from}–{to}. Δ1 = {f1} − {f0} = {d1}, Δ2 = {f1} − {f2} = {d2} → {from} + ({d1} ÷ ({d1} + {d2})) × {w} = {value}",
  workModeFlat:
    "Mode: modal class {from}–{to} has the same frequency as both neighbours, so Δ1 + Δ2 = 0 and the class midpoint, {value}, is used.",
  workDensityNote:
    "Class widths differ, so f₁, f₀ and f₂ above are frequency densities (frequency ÷ width).",
  copySummary: "Copy summary",
  copied: "Copied!",
  summaryText:
    "n = {n}, mean = {mean}, median = {median}, mode = {mode}, sample SD = {sd}, sample variance = {variance}",
} as const;

export default function GroupedDataCalculatorWidget() {
  const strings = useToolUi(GROUPED_DATA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [mode, setMode] = useState<BoundaryMode>("detect");

  const parsed = parseClassLines(raw);
  const result =
    parsed.rows.length > 0 ? groupedReport(parsed.rows, mode) : null;
  const report = result !== null && isGroupedReport(result) ? result : null;
  const problem = result !== null && !isGroupedReport(result) ? result : null;
  const num = (value: number) => formatStat(fmt, value);
  const view = { strings, fmt, num };
  const top = report ? headline(report, view) : null;

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(parsed.rows.length)}
              {tPlural(strings.classesDetected, locale, parsed.rows.length)}
              {parsed.skipped.length > 0
                ? tPlural(strings.skippedHint, locale, parsed.skipped.length, {
                    lines: parsed.skipped.map((line) => fmt(line)).join(", "),
                  })
                : null}
            </>
          }
          id="gd-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <PillGroup
          label={strings.boundaryLabel}
          name="gd-boundaries"
          onChange={setMode}
          options={[
            { value: "detect", label: strings.boundaryDetect },
            { value: "asWritten", label: strings.boundaryWritten },
          ]}
          value={mode}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleData)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleLimits)}
            type="button"
          >
            {strings.tryLimits}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={raw === ""}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {problem === "overlap" ? (
        <ErrorNotice>{strings.overlap}</ErrorNotice>
      ) : null}
      {problem === "noData" ? (
        <ErrorNotice>{strings.noData}</ErrorNotice>
      ) : null}
      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard label={strings.cardN} value={fmt(report.n)} />
              <StatCard
                emphasis
                label={strings.cardMean}
                value={num(report.mean)}
              />
              <StatCard
                emphasis
                label={strings.cardMedian}
                value={num(report.median.value)}
              />
              <StatCard emphasis label={strings.cardMode} value={top?.mode} />
              <StatCard label={strings.cardSd} value={top?.sd} />
              <StatCard label={strings.cardVariance} value={top?.variance} />
            </StatGrid>
            {report.equalWidths ? null : (
              <Notice>{strings.unequalNotice}</Notice>
            )}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySummary}
              text={t(strings.summaryText, {
                n: fmt(report.n),
                mean: num(report.mean),
                median: num(report.median.value),
                ...top,
              })}
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
            <ResultTable
              footer={tableTotals(report, view)}
              head={[
                strings.colClass,
                strings.colBoundaries,
                strings.colFreq,
                strings.colMidpoint,
                strings.colFx,
                strings.colFx2,
                strings.colCumulative,
              ]}
              rows={tableRows(report, view)}
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.statsTitle}</h3>
            <ResultTable
              head={[strings.colStatistic, strings.colValue]}
              rows={statisticRows(report, view)}
              wrapFirstColumn
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
            <GroupedPlot report={report} view={view} />
            <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
          </div>

          <GroupedWorking report={report} view={view} />
        </section>
      ) : null}
      {report || problem ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
